/*
 * File: base-uitag.ts
 * File Created: 2024-01-03 09:50:56
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2024-11-21 05:09:18
 * Modified By: Val Liu
 * -----
 */

import { ObjectHelper, StringHelper } from "@root/core";
import {
    EUITagRuleType,
    EUITagRuleUnion,
    EUITagTarget,
    IUITag,
    IUITagInput,
    IUITagResult,
} from "./core";
import { executeRuleFunction, parseRuleFunction } from "./parse-rule-function";

export abstract class BaseUITag implements IUITag {
    abstract appliedTo: EUITagTarget[];
    abstract ruleTypes: EUITagRuleUnion[];
    abstract allowNullRule: boolean;

    abstract parse(input: IUITagInput): IUITagResult | null;

    parseRule(
        input: IUITagInput,
        ruleTypes: EUITagRuleUnion[] = this.ruleTypes,
    ): NonNullable<unknown> | null {
        let { rule, component } = input;
        const { element } = input.variables;
        /// clone the ruleTypes, since Function will be removed
        ruleTypes = [...ruleTypes];

        /// handle null value
        if (rule == null) {
            if (!this.allowNullRule && !input.isDefault) {
                console.error(`<${this.constructor.name}> doesn't allow null rule.`);
            }
            return rule;
        }
        /// handle default value
        if (ruleTypes == undefined || ruleTypes.length == 0) return rule;

        /// parsing by ruleTypes
        let result: any = undefined;
        doParse: do {
            /// 1) parse the rule as function
            let ruleFunc = typeof rule === "function" ? rule : parseRuleFunction(input);
            if (ruleFunc != null) {
                /// 1.1) bypass pure "Function"
                if (ruleTypes[0] === EUITagRuleType.Function) {
                    result = ruleFunc;
                    break;
                }
                /// 1.2) else, do FunctionExecute
                rule = ruleFunc.call(component, input.inputs.props, element);
                if (rule === null) {
                    /// break if null (null = has value, undefined = no value)
                    result = rule;
                    break;
                }
                /// 1.3) later validate the rule value
            }
            /// remove the "Function" if at the first place
            if (ruleTypes[0] === EUITagRuleType.Function) ruleTypes.shift();

            try {
                result = testRules(input, ruleTypes, rule);
            } catch (e) {
                console.error(`<${this.constructor.name}>`, e);
            }
        } while (0);

        /// the final result should not be undefined,
        /// null is ok.
        if (result === undefined) {
            console.error(
                `<${this.constructor.name}> error parsing input.`,
                `The attribute <${input.meta.attr.name}>'s rule return "undefined".`,
                input,
            );
        }

        return result;
    }
}

function testRules(input: IUITagInput, ruleTypes: EUITagRuleUnion[], rule: any) {
    let result: any = undefined;
    doParse: do {
        for (let ruleType of ruleTypes) {
            result = testRule(input, ruleType, rule);

            /// if the result is not undefined (null is ok),
            /// break the loop return it.
            if (result !== undefined) break doParse;
        }
    } while (0);
    return result;
}

function testRule(input: IUITagInput, ruleType: EUITagRuleUnion, rule: any) {
    const value = input.inputs.props?.value;
    const meta = input.meta;
    let result: any = undefined;

    /// handle Array ruleType
    if (Array.isArray(ruleType) && Array.isArray(rule)) {
        try {
            let arrayElement = Array.isArray(rule)
                ? rule
                : typeof rule === "string"
                  ? StringHelper.toObject(rule) ?? undefined
                  : undefined;
            if (arrayElement != undefined) {
                result = rule.map((obj) => {
                    return testRules(input, ruleType, obj);
                });
            }
        } catch (e) {
            /// don't catch
        }
        return result;
    }

    switch (ruleType) {
        case EUITagRuleType.Object: {
            if (typeof rule === "string") {
                result = StringHelper.toObject(rule) ?? undefined;
            } else if (ObjectHelper.isPureObject(rule)) {
                result = rule;
            }
            break;
        }
        case EUITagRuleType.String: {
            if (typeof rule === "string") {
                result = rule;
            }
            break;
        }
        case EUITagRuleType.Date: {
            if (typeof rule === "string") {
                result = StringHelper.toDate(rule) ?? undefined;
            } else if (rule instanceof Date) {
                result = rule;
            }
            break;
        }
        case EUITagRuleType.Number: {
            if (typeof rule === "string") {
                result = StringHelper.toNumber(rule) ?? undefined;
            } else if (typeof rule === "number") {
                result = rule;
            }
            break;
        }
        case EUITagRuleType.Boolean: {
            if (typeof rule === "string") {
                result = StringHelper.toBoolean(rule) ?? undefined;
            } else if (typeof rule === "boolean") {
                result = rule;
            }
            break;
        }
        case EUITagRuleType.Function: {
            throw `error tag rule position: "Function" should be in front of rules.`;
        }
        case EUITagRuleType.Meta: {
            const { name, isArray } = meta.attr.type;
            /// parse by the type name
            switch (name) {
                case "string": {
                    result = testRule(
                        input,
                        isArray ? [EUITagRuleType.String] : EUITagRuleType.String,
                        rule,
                    );
                    break;
                }
                case "number": {
                    result = testRule(
                        input,
                        isArray ? [EUITagRuleType.Number] : EUITagRuleType.Number,
                        rule,
                    );
                    break;
                }
                case "boolean": {
                    result = testRule(
                        input,
                        isArray ? [EUITagRuleType.Boolean] : EUITagRuleType.Boolean,
                        rule,
                    );
                    break;
                }
                case "Date": {
                    result = testRule(
                        input,
                        isArray ? [EUITagRuleType.Date] : EUITagRuleType.Date,
                        rule,
                    );
                    break;
                }
                default: {
                    /// Val: don't parse the "any" type!
                    result = rule;
                    break;
                }
            }
            break;
        }
        case EUITagRuleType.Custom: {
            result = rule;
            break;
        }
        default:
            throw `ruleType "${ruleType}" not defined.`;
    }

    return result;
}
